/**
 * API client — thin wrapper around fetch for the FastAPI backend.
 * In dev mode, Vite proxies /students, /questions, etc. to localhost:8000.
 * In production:
 *   - same-origin deploy → leave VITE_API_URL unset (BASE='')
 *   - cross-origin deploy (e.g. Netlify frontend → Render backend) → set
 *     VITE_API_URL=https://your-backend.onrender.com at build time.
 */

import type { Question } from '../types';
import { wakeApi } from './wakeApi';

const BASE = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
const API_TIMEOUT = 15000; // 15 seconds
const MAX_RETRIES = 2;
const RETRY_DELAY = 1000;

interface ApiOptions {
  method?: string;
  body?: unknown;
  token?: string | null;
  timeout?: number;
  retries?: number;
}

/**
 * "Failed to fetch / TypeError" with no other info on a cross-origin call
 * almost always means the Render dyno is asleep and returned 503 with no
 * CORS headers — which the browser blocks BEFORE handing us a response.
 * This heuristic flag triggers a single wakeApi() retry pass.
 */
function looksLikeColdStart(err: Error): boolean {
  const status = (err as Error & { status?: number }).status;
  if (status === 503 || status === 502 || status === 504) return true;
  const msg = err.message || '';
  return /failed to fetch|networkerror|load failed|cors/i.test(msg);
}

let wakeInFlight: Promise<boolean> | null = null;
async function dedupedWake(): Promise<boolean> {
  if (!BASE) return true;
  if (!wakeInFlight) {
    wakeInFlight = wakeApi(BASE).finally(() => { wakeInFlight = null; });
  }
  return wakeInFlight;
}

async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, token, timeout = API_TIMEOUT, retries = method === 'GET' ? MAX_RETRIES : 0 } = opts;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let lastError: Error | null = null;
  let didWake = false;

  for (let attempt = 0; attempt <= retries + 1; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const res = await fetch(`${BASE}${path}`, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: res.statusText }));
        const error = new Error(err.detail || `API ${res.status}`) as Error & { status?: number };
        error.status = res.status;
        // Don't retry client errors (4xx) — only retry server/network errors
        if (res.status >= 400 && res.status < 500) throw error;
        lastError = error;
        // Cold-start: wake once then retry without consuming a normal retry slot
        if (looksLikeColdStart(error) && !didWake) {
          didWake = true;
          await dedupedWake();
          continue;
        }
        if (attempt < retries) {
          await new Promise(r => setTimeout(r, RETRY_DELAY * (attempt + 1)));
        }
        continue;
      }

      return await res.json();
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));
      if (lastError.name === 'AbortError') {
        lastError = new Error('הבקשה נכשלה — timeout');
      }
      const status = (lastError as Error & { status?: number }).status;
      // 4xx = client error, never retry
      if ((status && status >= 400 && status < 500) || lastError.message === 'Student not found') throw lastError;
      // Cold-start: wake once then retry without consuming a normal retry slot
      if (looksLikeColdStart(lastError) && !didWake) {
        didWake = true;
        await dedupedWake();
        continue;
      }
      if (attempt < retries) {
        await new Promise(r => setTimeout(r, RETRY_DELAY * (attempt + 1)));
      }
    }
  }
  throw lastError!;
}

// ── Auth ──────────────────────────────────────────────────────────

/** Map backend user object → frontend Student shape */
function mapUser(raw: Record<string, unknown>): Record<string, unknown> {
  return {
    id: raw.student_id || raw.user_id || raw.id || '',
    name: raw.display_name || raw.name || '',
    email: raw.email || '',
    track: raw.track || raw.track_id || '',
    role: raw.role || 'student',
    avatar: raw.avatar || '',
    created_at: raw.created_at || '',
  };
}

/** Normalize auth response: backend sends { user, token }, frontend expects { student, token } */
function mapAuthResponse(res: Record<string, unknown>): { token: string; student: Record<string, unknown> } {
  const token = (res.token || '') as string;
  const rawUser = (res.user || res.student || {}) as Record<string, unknown>;
  return { token, student: mapUser(rawUser) };
}

export async function loginLocal(email: string, password: string) {
  const raw = await api<Record<string, unknown>>('/auth/login', {
    method: 'POST',
    body: { email, password },
  });
  return mapAuthResponse(raw);
}

export async function loginGoogle(credential: string, userinfo?: { email: string; name: string }) {
  const raw = await api<Record<string, unknown>>('/auth/google', {
    method: 'POST',
    body: { id_token: credential, ...(userinfo ? { userinfo } : {}) },
  });
  return mapAuthResponse(raw);
}

export async function registerStudent(data: {
  name: string;
  email: string;
  password: string;
  track: string;
}) {
  const raw = await api<Record<string, unknown>>('/auth/register', {
    method: 'POST',
    body: {
      display_name: data.name,
      email: data.email,
      password: data.password,
      track: data.track,
    },
  });
  return mapAuthResponse(raw);
}

// ── Questions ────────────────────────────────────────────────────

/** Extract human-readable source label from source object/array */
function extractSourceLabel(source: unknown): string {
  if (!source) return '';
  // Array of source_refs objects
  if (Array.isArray(source)) {
    const first = source[0];
    if (first && typeof first === 'object') {
      const obj = first as Record<string, unknown>;
      // Detailed source: "שאלון 382, קיץ תשפ"ד מועד א', שאלה 2"
      if (obj.original_ref) return String(obj.original_ref);
      // source_refs: "מעובד ממשפחות שאלות..."
      if (obj.label_he) {
        const year = obj.year ? ` (${obj.year})` : '';
        return `${obj.label_he}${year}`;
      }
    }
    return '';
  }
  // Single source object with exam/year/moed
  if (typeof source === 'object') {
    const obj = source as Record<string, unknown>;
    if (obj.original_ref) return String(obj.original_ref);
    if (obj.exam) {
      const parts = [String(obj.exam)];
      if (obj.moed) parts.push(String(obj.moed));
      if (obj.year) parts.push(String(obj.year));
      if (obj.question_number) parts.push(`שאלה ${obj.question_number}`);
      return parts.join(', ');
    }
    if (obj.label_he) return String(obj.label_he);
    return '';
  }
  // Plain string
  if (typeof source === 'string') return source;
  return '';
}

/** Map backend question shape → frontend Question type */
function mapQuestion(raw: Record<string, unknown>): Question {
  const correctAnswer = String(raw.correct_answer || '').trim();
  const rawDistractors = (raw.distractors || []) as unknown[];
  // Filter out empty/invalid distractors and ensure they're strings
  const distractors = rawDistractors
    .map(d => String(d || '').trim())
    .filter(d => d.length > 0 && d !== correctAnswer);

  // Build options array: correct answer + distractors, shuffled
  // Guard: ensure correct answer exists and we have at least 1 distractor
  const options = correctAnswer
    ? [correctAnswer, ...distractors]
    : distractors.length > 0 ? distractors : ['—'];

  // Simple shuffle
  for (let i = options.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [options[i], options[j]] = [options[j], options[i]];
  }
  // Safe index: fallback to 0 if correctAnswer somehow not found
  const correctIdx = Math.max(0, options.indexOf(correctAnswer));

  // Safely convert string-or-array fields to arrays
  const toArray = (val: unknown): string[] => {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string' && val.trim()) return val.split('\n').filter(Boolean);
    return [];
  };

  return {
    id: String(raw.question_id || raw.id || ''),
    question: String(raw.prompt_he || raw.question || ''),
    options,
    correct: correctIdx,
    topic: String(raw.topic || ''),
    difficulty: Number(raw.difficulty || 3),
    hint: String(raw.hint_he || ''),
    solution_steps: toArray(raw.hint_levels || raw.solution_steps),
    alt_methods: toArray(raw.alt_methods),
    tip: String(raw.tip || ''),
    rule: String(raw.rule_he || raw.rule || ''),
    source_exam: extractSourceLabel(raw.source) || String(raw.source_exam || ''),
    track: String(raw.track || ''),
    unit: String(raw.unit || ''),
  };
}

// PATCH /students/{id}/preferences — persists track + topic prefs so the
// parent view (which reads from the backend Student record) sees the
// current grade, not the original default '3u'.
export async function updateStudentPreferences(
  studentId: string,
  prefs: { track_id?: string; practice_topic_ids?: string[] },
  token: string,
): Promise<{ student_id: string; track_id: string; practice_topic_ids: string[] }> {
  return api(`/students/${studentId}/preferences`, {
    method: 'PATCH',
    body: prefs,
    token,
  });
}

export async function fetchQuestions(params?: Record<string, string>, token?: string | null): Promise<Question[]> {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  const raw = await api<Record<string, unknown>[]>(`/questions/random${qs}`, { token });
  // Map and filter out malformed questions (no text or < 2 options)
  return raw
    .map(mapQuestion)
    .filter(q => q.question.length > 0 && q.options.length >= 2);
}

export interface ApiPracticeUnit {
  unit_id: string;
  title: string;
  question_count: number;
  domain_id?: string;
  domain_name_he?: string;
}

export async function fetchPracticeUnits(track: string): Promise<ApiPracticeUnit[]> {
  const raw = await api<Record<string, unknown>>(`/practice-units?track=${track}`);
  const units = (raw.units || raw) as Record<string, unknown>[];
  return (Array.isArray(units) ? units : []).map((u) => ({
    unit_id: String(u.topic_id || u.unit_id || u.id || ''),
    title: String(u.name_he || u.title || u.name || ''),
    question_count: Number(u.question_count || 0),
    domain_id: u.domain_id ? String(u.domain_id) : undefined,
    domain_name_he: u.domain_name_he ? String(u.domain_name_he) : undefined,
  }));
}

// ── Curriculum (Lesson Mode) ─────────────────────────────────────

export interface CurriculumTopicSummary {
  topic_id: string;
  title_he: string;
  language: 'quantity' | 'shape' | 'change' | 'uncertainty';
  tracks: string[];
  linked_skill_topics: string[];
  /** First sentence of plain_explanation, capped at ~140 chars. */
  plain_preview?: string;
}

export interface CurriculumTopic extends CurriculumTopicSummary {
  plain_explanation: string;
  philosophy: string;
  logic: string;
  formulas: string[];
  example: string;
  common_mistakes: string[];
  where_in_bagrut: { '3u'?: string; '4u'?: string; '5u'?: string } | string;
  platform_idea: string;
  // ── v1.39.2 lesson-bank rich fields (optional; render-if-present) ──
  big_idea_he?: string;
  learning_objectives_he?: string[];
  guided_practice?: { level?: string; question?: string }[];
  graduated_hints_he?: string[];
  differentiation_he?: { support?: string; standard?: string; challenge?: string };
  platform_widgets_he?: string[];
  mastery_rubric_he?: {
    recognize?: string;
    perform?: string;
    explain?: string;
    transfer?: string;
  };
  duration_minutes?: number;
  source_difficulty?: string;
}

export async function fetchCurriculumTopics(filters?: {
  /** Any Track value — elementary (el1–el6), middle (ms7–ms9), Bagrut (3u–5u). */
  track?: string;
  language?: CurriculumTopicSummary['language'];
}): Promise<CurriculumTopicSummary[]> {
  const params = new URLSearchParams();
  if (filters?.track) params.set('track', filters.track);
  if (filters?.language) params.set('language', filters.language);
  const qs = params.toString();
  const raw = await api<{ count: number; topics: CurriculumTopicSummary[] }>(
    `/curriculum${qs ? '?' + qs : ''}`
  );
  return raw.topics;
}

export async function fetchTopic(topicId: string): Promise<CurriculumTopic> {
  return api<CurriculumTopic>(`/curriculum/${topicId}`);
}

// ── Admin platform analytics (admin role required) ─────────────

export interface PlatformAnalytics {
  generated_at: number;
  users: {
    total: number;
    by_role: Record<string, number>;
    new_7d: number;
    new_30d: number;
  };
  activity: {
    total_attempts_ever: number;
    attempts_24h: number;
    attempts_7d: number;
    attempts_30d: number;
    active_students_7d: number;
    active_students_30d: number;
  };
  study_time: {
    total_seconds: number;
    last_7d_seconds: number;
    last_24h_seconds: number;
  };
  daily_activity_30d: { date: string; attempts: number; active_students: number }[];
  top_students: { student_id: string; name: string; track: string | null; attempts: number }[];
  // ── Extended analytics (v1.27.1+) ──────────────────────────────
  by_track?: {
    track: string;
    group: 'elementary' | 'middle_school' | 'bagrut' | 'unknown';
    users: number;
    active_30d: number;
    attempts_30d: number;
    accuracy_30d: number | null;
    avg_time_sec: number | null;
    hints_per_attempt: number | null;
  }[];
  by_group?: {
    group: string;
    users: number;
    active_30d: number;
    attempts_30d: number;
    accuracy_30d: number | null;
    avg_time_sec: number | null;
  }[];
  topic_insights?: {
    topic: string;
    attempts: number;
    accuracy: number;
    avg_time_sec: number;
    hint_rate: number;
  }[];
  worst_questions?: {
    question_id: string;
    prompt_he: string;
    topic: string;
    track: string;
    attempts: number;
    accuracy: number;
    avg_time_sec: number;
    hints_per_attempt: number;
  }[];
  engagement?: {
    return_rate_pct: number | null;
    avg_attempt_time_sec: number;
    hint_rate_7d_pct: number;
  };
  // ── v1.29.1 cohort & growth breakdowns ────────────────────────
  signups_30d?: { date: string; count: number }[];
  activity_by_group_30d?: {
    date: string;
    elementary: number;
    middle_school: number;
    bagrut: number;
  }[];
  hour_distribution_30d?: {
    hour: number;
    elementary: number;
    middle_school: number;
    bagrut: number;
  }[];
  rubric_distribution_by_group?: Record<
    string,
    { L0: number; L1: number; L2: number; L3: number; L4: number }
  >;
  parents?: {
    active_links: number;
    visited_7d: number;
  };
  // Private-tutor feature adoption
  tutors?: {
    total_tutors: number;
    active_links: number;
    tutors_with_tutees: number;
    pending_invites: number;
    invites_accepted_total: number;
    invite_conversion_pct: number | null;
  };
  // Screen / feature time-tracking
  feature_usage?: {
    by_feature: {
      feature: string;
      visits: number;
      total_minutes: number;
      avg_seconds: number;
      unique_users: number;
      minutes_7d: number;
    }[];
    top_feature: string | null;
    tracked_users: number;
  };
  // v1.31.3 — population-level mastery insights
  mastery?: {
    total_events: number;
    topics_with_any_mastery: number;
    by_topic: {
      topic: string;
      students: number;
      earliest: number;
      latest: number;
      last_7d: number;
      last_30d: number;
    }[];
  };
  // v1.38.1 — detailed per-user log + raw failure ranking + login summary
  users_log?: {
    user_id: string;
    email: string;
    display_name: string;
    role: string;
    track: string | null;
    group: string;
    tenant_id: string;
    created_at: number;
    login_count: number;
    last_login: number | null;
    sessions: number;
    attempts: number;
    correct: number;
    failures: number;
    accuracy_pct: number | null;
    time_seconds: number;
    hints_used: number;
    first_attempt_at: number | null;
    last_attempt_at: number | null;
    is_active: boolean;
  }[];
  most_failed_questions?: {
    question_id: string;
    prompt_he: string;
    topic: string;
    track: string;
    attempts: number;
    failures: number;
    accuracy: number;
    avg_time_sec: number;
  }[];
  login_summary?: {
    total_logins: number;
    users_logged_in_24h: number;
    users_logged_in_7d: number;
    never_logged_in: number;
  };
}

export async function fetchPlatformAnalytics(token: string): Promise<PlatformAnalytics> {
  return api<PlatformAnalytics>('/admin/platform-analytics', { token });
}

// ── Parent-view sharing token ──────────────────────────────────
export interface ParentLink {
  token: string;
  student_id: string;
  created_at: number;
}

export async function createParentLink(studentId: string, authToken: string): Promise<ParentLink> {
  return api<ParentLink>(`/students/${studentId}/parent-link`, { method: 'POST', token: authToken });
}

export async function revokeParentLink(studentId: string, authToken: string): Promise<{ status: string; revoked_count: string }> {
  return api<{ status: string; revoked_count: string }>(`/students/${studentId}/parent-link`, { method: 'DELETE', token: authToken });
}

// ── Feature-usage tracking (screen time) ───────────────────────
export interface FeatureUsageItem {
  feature: string;
  duration_sec: number;
}

export async function recordFeatureUsage(items: FeatureUsageItem[], authToken: string): Promise<{ status: string; recorded: number }> {
  return api('/telemetry/feature-usage', { method: 'POST', token: authToken, body: items });
}

// ── Switch account type (student ↔ teacher) ────────────────────
export async function switchRole(role: 'student' | 'teacher', authToken: string): Promise<{ status: string; role: string; user?: unknown; token: string }> {
  return api('/auth/switch-role', { method: 'POST', token: authToken, body: { role } });
}

// ── Account restore (within 30-day soft-delete grace) ──────────
export async function restoreAccount(email: string, password: string): Promise<{ status: string; user: unknown; token: string }> {
  return api('/auth/restore', { method: 'POST', body: { email, password } });
}

// ── Password reset ─────────────────────────────────────────────
export async function requestPasswordReset(email: string): Promise<{ status: string; message: string }> {
  return api('/auth/password-reset/request', { method: 'POST', body: { email } });
}

export async function confirmPasswordReset(token: string, newPassword: string): Promise<{ status: string }> {
  return api('/auth/password-reset/confirm', {
    method: 'POST',
    body: { token, new_password: newPassword },
  });
}

// ── Private tutor: invite / accept / view ──────────────────────
export interface TutorInvite {
  code: string;
  expires_at: number;
  share_message_he: string;
}

export interface TutoreeSummary {
  student_id: string;
  name: string;
  track: string;
  track_group: string;
  summary_30d: { attempts: number; correct: number; accuracy_pct: number | null; minutes: number };
  mastered_topics: number;
}

export interface TutoreesList {
  students: TutoreeSummary[];
  pending_invites: { code: string; created_at: number; expires_at: number }[];
}

export interface TutorOverview {
  student: { student_id: string; name: string; track: string; track_group: string };
  summary_7d: { attempts: number; correct: number; accuracy_pct: number | null; minutes: number };
  summary_30d: { attempts: number; correct: number; accuracy_pct: number | null; minutes: number };
  summary_total: { attempts: number; correct: number; accuracy_pct: number | null; minutes: number };
  activity_30d: { date: string; attempts: number; correct: number }[];
  topics: { topic: string; attempts: number; correct: number; accuracy_pct: number; minutes: number; rubric: number }[];
  mastered_events: MasteryEvent[];
}

export async function createTutorInvite(authToken: string): Promise<TutorInvite> {
  return api<TutorInvite>('/tutor/invites', { method: 'POST', token: authToken });
}

export async function fetchMyTutees(authToken: string): Promise<TutoreesList> {
  return api<TutoreesList>('/tutor/students', { token: authToken });
}

export async function fetchTutoreeOverview(studentId: string, authToken: string): Promise<TutorOverview> {
  return api<TutorOverview>(`/tutor/students/${studentId}/overview`, { token: authToken });
}

export async function acceptTutorCode(studentId: string, code: string, authToken: string): Promise<{ status: string; teacher_user_id: string; accepted_at: number }> {
  return api(`/students/${studentId}/accept-tutor`, {
    method: 'POST',
    token: authToken,
    body: { code },
  });
}

export async function fetchMyTutors(studentId: string, authToken: string): Promise<{ tutors: { teacher_user_id: string; accepted_at: number }[] }> {
  return api(`/students/${studentId}/tutors`, { token: authToken });
}

export async function revokeTutor(studentId: string, teacherUserId: string, authToken: string): Promise<{ status: string }> {
  return api(`/students/${studentId}/tutors/${teacherUserId}`, { method: 'DELETE', token: authToken });
}

// ── Mastery events (server-persisted L4 achievements) ──────────
export interface MasteryEvent {
  topic: string;
  track: string;
  achieved_at: number; // seconds epoch
}

export async function recordMastery(
  studentId: string,
  topic: string,
  track: string,
  authToken: string,
): Promise<{ status: string; already: boolean; event: MasteryEvent }> {
  return api(`/students/${studentId}/mastery`, {
    method: 'POST',
    body: { topic, track },
    token: authToken,
  });
}

// ── Learning Path (milestone-based progression) ─────────────────

export interface PathMilestone {
  milestone_id: string;
  name_he: string;
  domain_id: string;
  topic_ids: string[];
  skill_ids: string[];
  requires: string[];
  mastery_threshold: number;
  estimated_sessions: number;
  status: 'locked' | 'active' | 'completed';
}

export interface LearningPathDto {
  student_id: string;
  track_id: string;
  completion_pct: number;
  total_milestones: number;
  completed_milestones: number;
  current_milestone: PathMilestone | null;
  milestones: PathMilestone[];
}

/** Returns null if no path is built yet for this student (404 on backend). */
export async function fetchLearningPath(studentId: string): Promise<LearningPathDto | null> {
  try {
    return await api<LearningPathDto>(`/students/${encodeURIComponent(studentId)}/learning-path`);
  } catch (err) {
    if (err instanceof Error && /not built|404|not found/i.test(err.message)) return null;
    throw err;
  }
}

// ── Distractor diagnoses (per wrong-answer mistake explanation) ──

export interface DistractorDiagnosis {
  distractor_index: number;
  distractor_value: string;
  mistake_label: string;
  explanation_he: string;
  quality: 'good' | 'ambiguous' | 'weak_generated';
}

export interface QuestionDiagnoses {
  question_id: string;
  diagnoses: DistractorDiagnosis[];
  covered: boolean;
}

/** Fetch per-distractor mistake diagnoses for a question. Returns empty list if not covered. */
export async function fetchQuestionDiagnoses(questionId: string): Promise<QuestionDiagnoses> {
  return api<QuestionDiagnoses>(`/questions/${encodeURIComponent(questionId)}/diagnoses`);
}

export interface ReviewBatchQuestion {
  question_id: string;
  prompt_he: string;
  correct_answer: string;
  topic: string;
  difficulty: number;
  solution_steps_he: string[];
  rule_he: string;
}

export interface ReviewBatchResponse {
  questions: ReviewBatchQuestion[];
  found: number;
  requested: number;
}

/** Batch-fetch slim question payloads for the Mistakes Review screen.
 *  Caps at 50 ids server-side; missing ids are silently dropped. */
export async function fetchReviewBatch(ids: string[]): Promise<ReviewBatchResponse> {
  if (ids.length === 0) return { questions: [], found: 0, requested: 0 };
  return api<ReviewBatchResponse>('/questions/review-batch', {
    method: 'POST',
    body: { ids },
  });
}

// ── Student Data ─────────────────────────────────────────────────
export async function fetchDashboard(studentId: string, token: string) {
  return api<Record<string, unknown>>(`/students/${studentId}/dashboard`, { token });
}

export async function fetchGamification(studentId: string, token: string) {
  return api<Record<string, unknown>>(`/students/${studentId}/gamification`, { token });
}

export interface LeaderboardEntry {
  student_id: string;
  name: string;
  xp: number;
  level: number;
  rank: number;
}

export async function fetchLeaderboard(token: string, limit = 20): Promise<{ leaderboard: LeaderboardEntry[]; total: number }> {
  return api<{ leaderboard: LeaderboardEntry[]; total: number }>(`/leaderboard?limit=${limit}`, { token });
}

// ── Attempts ─────────────────────────────────────────────────────
export async function submitAttempt(
  data: {
    student_id: string;
    question_id: string;
    selected: number;
    correct: boolean;
    time_ms: number;
    hint_used: boolean;
    session_id?: string;
    student_answer?: string;
  },
  token: string
) {
  // Don't submit attempts for guest users — backend will 404
  if (!data.student_id || data.student_id === 'guest') return null;

  try {
    return await api<Record<string, unknown>>('/attempts', {
      method: 'POST',
      body: {
        student_id: data.student_id,
        question_id: data.question_id,
        student_answer: data.student_answer || String(data.selected),
        session_id: data.session_id || `practice-${Date.now()}`,
        time_spent_sec: Math.round(data.time_ms / 1000),
        hints_used: data.hint_used ? 1 : 0,
        is_skipped: false,
        stalled: false,
      },
      token,
      // POST default is 0 retries. Bump to 1 so a single cold-start CORS
      // failure on a long-running practice session auto-recovers via the
      // wake-then-retry path in api(), instead of throwing into the silent
      // catch and losing the attempt server-side.
      retries: 1,
    });
  } catch {
    // Silently fail — attempts are also tracked locally
    return null;
  }
}

// ── Exam ─────────────────────────────────────────────────────────

export interface TopicInfo {
  topic: string;
  count: number;
  exam_relevance: string;
}

export interface TopicsResponse {
  track: string;
  total_questions: number;
  domains: Record<string, TopicInfo[]>;
}

export async function fetchTopics(track: string): Promise<TopicsResponse> {
  return api<TopicsResponse>(`/questions/topics?track=${track}`);
}

export async function fetchQuestionsByTopic(track: string, topic: string, count: number = 30): Promise<Question[]> {
  const raw = await api<Record<string, unknown>[]>(
    `/questions/random?track=${track}&topic=${encodeURIComponent(topic)}&count=${count}`
  );
  return raw
    .map(mapQuestion)
    .filter(q => q.question.length > 0 && q.options.length >= 2);
}

export async function fetchExamQuestions(track: string, count: number) {
  return api<unknown[]>(`/questions?track=${track}&limit=${count}&exam=true`);
}

export async function submitExamSession(
  data: Record<string, unknown>,
  token: string
) {
  return api<Record<string, unknown>>('/sessions', {
    method: 'POST',
    body: data,
    token,
  });
}

export default api;
